import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useLocation } from 'wouter';
import { ArrowLeft } from 'lucide-react';
import { COMPANY } from '@/config';

export default function DataDeletion() {
  const [ , navigate ] = useLocation();

  return (
    <div className="min-h-screen bg-[#0E1525]">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <Card className="shadow-2xl border border-slate-700 bg-[#1E293B]">
            <CardHeader className="text-center space-y-4">
              <div className="flex items-center justify-between">
                <Button
                  variant="ghost"
                  onClick={() => navigate('/')}
                  className="flex items-center gap-2 text-gray-400 hover:text-white hover:bg-slate-700"
                >
                  <ArrowLeft className="h-4 w-4"/>
                  Back
                </Button>
                <div className="flex-1"/>
              </div>
              <CardTitle className="text-3xl font-bold text-white">
                Data Deletion Instructions
              </CardTitle>
              <p className="text-sm text-gray-400">
                How to Request Deletion of Your Data
              </p>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[70vh] pr-4">
                <div className="space-y-6 text-gray-300">
                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">Your Right to Data Deletion</h2>
                    <p>
                      We respect your privacy and your right to have your personal data deleted. If you wish to delete your account and all associated data from our service, you can do so by following the instructions below.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">What Data Will Be Deleted</h2>
                    <p>When you request account deletion, we will permanently remove:</p>
                    <ul className="list-disc pl-6 mt-2 space-y-1">
                      <li>Your account information (name, email, profile data)</li>
                      <li>Your profile picture/avatar</li>
                      <li>Your preferences and settings (gender preferences, age group, location filters)</li>
                      <li>Your subscription information (if applicable)</li>
                      <li>Any session metadata associated with your account</li>
                      <li>Authentication tokens and credentials</li>
                    </ul>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">What Data Is NOT Stored</h2>
                    <p>The following data is never stored on our servers and therefore doesn&apos;t need deletion:</p>
                    <ul className="list-disc pl-6 mt-2 space-y-1">
                      <li>Video or audio content from your calls (peer-to-peer connections only)</li>
                      <li>Text chat messages (not stored on servers)</li>
                      <li>Call recordings or screenshots (we don&apos;t record anything)</li>
                    </ul>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">How to Request Data Deletion</h2>

                    <div className="bg-slate-800 rounded-lg p-6 mt-4">
                      <h3 className="text-lg font-medium text-white mb-4">Method 1: Email Request (Recommended)</h3>
                      <p className="mb-3">Send an email to our support team with your deletion request:</p>
                      <div className="bg-slate-900 rounded p-4 mb-3">
                        <p className="font-mono text-blue-400">{COMPANY.supportEmail}</p>
                      </div>
                      <p className="text-sm text-gray-400 mb-3">Please include in your email:</p>
                      <ul className="list-disc pl-6 space-y-1 text-sm">
                        <li>Subject line: &quot;Account Deletion Request&quot;</li>
                        <li>The email address associated with your account</li>
                        <li>The name on your account</li>
                        <li>The authentication method you used (Google, Facebook, or VK)</li>
                        <li>Confirmation that you want to permanently delete all your data</li>
                      </ul>
                    </div>

                    <div className="bg-slate-800 rounded-lg p-6 mt-4">
                      <h3 className="text-lg font-medium text-white mb-4">Method 2: Self-Service Account Deletion</h3>
                      <p className="mb-3">
                        You can delete your account directly from the application:
                      </p>
                      <ul className="list-disc pl-6 space-y-1 text-sm">
                        <li>Log in to your account</li>
                        <li>Navigate to Account Settings</li>
                        <li>Click the &quot;Delete Account&quot; button</li>
                        <li>Confirm your deletion request</li>
                        <li>Your account will be deleted immediately</li>
                      </ul>
                      <p className="text-sm text-yellow-400 mt-3 flex items-start gap-2">
                        <span>⚠️</span>
                        <span>This action is permanent and cannot be undone. All your data will be deleted immediately.</span>
                      </p>
                    </div>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">Deletion Timeline</h2>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">
                          1
                        </div>
                        <div>
                          <h4 className="font-medium text-white">Request Received</h4>
                          <p className="text-sm text-gray-400">We receive your deletion request via email</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">
                          2
                        </div>
                        <div>
                          <h4 className="font-medium text-white">Identity Verification (1-2 business days)</h4>
                          <p className="text-sm text-gray-400">
                            We verify your identity to ensure the request is legitimate and from the actual account owner
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">
                          3
                        </div>
                        <div>
                          <h4 className="font-medium text-white">Account Deletion (Immediate)</h4>
                          <p className="text-sm text-gray-400">
                            Once verified, your account is immediately deactivated and marked for deletion
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">
                          4
                        </div>
                        <div>
                          <h4 className="font-medium text-white">Data Purge (Within 30 days)</h4>
                          <p className="text-sm text-gray-400">
                            All personal data is permanently deleted from our systems within 30 days
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="bg-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">
                          ✓
                        </div>
                        <div>
                          <h4 className="font-medium text-white">Confirmation Email</h4>
                          <p className="text-sm text-gray-400">
                            You will receive a confirmation email once the deletion is complete
                          </p>
                        </div>
                      </div>
                    </div>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">Important Notes</h2>
                    <div className="bg-yellow-900/20 border border-yellow-700 rounded-lg p-4 space-y-2">
                      <p className="flex items-start gap-2">
                        <span className="text-yellow-500 font-bold">⚠️</span>
                        <span>
                          <strong>Account deletion is permanent and irreversible.</strong> Once deleted, you cannot recover your account or any associated data.
                        </span>
                      </p>
                      <p className="flex items-start gap-2">
                        <span className="text-yellow-500 font-bold">⚠️</span>
                        <span>
                          If you have an active subscription, it will be cancelled immediately upon account deletion. No refunds will be provided for unused subscription time.
                        </span>
                      </p>
                      <p className="flex items-start gap-2">
                        <span className="text-yellow-500 font-bold">⚠️</span>
                        <span>
                          Some data may be retained for legal compliance purposes (e.g., transaction records for tax purposes) as required by law, typically for 7 years.
                        </span>
                      </p>
                    </div>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">Data Retention Exceptions</h2>
                    <p>In limited cases, some data may be retained even after account deletion:</p>
                    <ul className="list-disc pl-6 mt-2 space-y-1">
                      <li>
                        <strong>Legal Compliance:</strong> Transaction records and payment information may be retained for tax and legal compliance (typically 7 years)
                      </li>
                      <li>
                        <strong>Security and Fraud Prevention:</strong> Ban records and abuse reports may be retained to prevent re-registration of banned users
                      </li>
                      <li>
                        <strong>Backup Systems:</strong> Data in backup systems may persist for up to 90 days before being purged
                      </li>
                    </ul>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">Alternative: Account Deactivation</h2>
                    <p>
                      If you&apos;re not ready for permanent deletion, you can simply stop using the service. Since we don&apos;t store video calls or chat messages, and your account requires active login, you can just:
                    </p>
                    <ul className="list-disc pl-6 mt-2 space-y-1">
                      <li>Log out of your account</li>
                      <li>Revoke access from your OAuth provider (Google, Facebook, or VK)</li>
                      <li>Clear your browser data</li>
                    </ul>
                    <p className="mt-2">
                      Your account will remain inactive but can be reactivated if you log in again.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">Third-Party Data</h2>
                    <p>
                      If you authenticated using Google, Facebook, or VK, deleting your account with us does not delete your data from those services. To delete data from third-party providers:
                    </p>
                    <ul className="list-disc pl-6 mt-2 space-y-1">
                      <li>
                        <strong>Google:</strong> Visit{' '}
                        <a
                          href="https://myaccount.google.com/permissions"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-400 hover:underline"
                        >
                          Google Account Permissions
                        </a>{' '}
                        and revoke access to our app
                      </li>
                      <li>
                        <strong>Facebook:</strong> Visit{' '}
                        <a
                          href="https://www.facebook.com/settings?tab=applications"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-400 hover:underline"
                        >
                          Facebook Apps and Websites
                        </a>{' '}
                        and remove our app
                      </li>
                      <li>
                        <strong>VK:</strong> Visit{' '}
                        <a
                          href="https://vk.com/settings?act=apps"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-400 hover:underline"
                        >
                          VK Applications
                        </a>{' '}
                        and revoke access to our app
                      </li>
                    </ul>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">Questions or Issues?</h2>
                    <p>If you have questions about the data deletion process or encounter any issues, contact us:</p>
                    <div className="bg-slate-800 rounded-lg p-4 mt-3">
                      <ul className="space-y-2">
                        <li>
                          <strong className="text-white">Email:</strong>{' '}
                          <a href={`mailto:${COMPANY.supportEmail}`} className="text-blue-400 hover:underline">
                            {COMPANY.supportEmail}
                          </a>
                        </li>
                      </ul>
                    </div>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">Related Resources</h2>
                    <ul className="space-y-2">
                      <li>
                        <a href="/privacy" className="text-blue-400 hover:underline">
                          Privacy Policy
                        </a>{' '}
                        - Learn about how we collect and use your data
                      </li>
                      <li>
                        <a href="/terms" className="text-blue-400 hover:underline">
                          Terms and Conditions
                        </a>{' '}
                        - Review our terms of service
                      </li>
                    </ul>
                  </section>

                  <div className="mt-8 p-4 bg-slate-800 rounded-lg">
                    <p className="text-sm text-gray-300 font-medium">
                      We take your privacy seriously and are committed to handling your data deletion request promptly and completely. If you have any concerns about the deletion process, please don&apos;t hesitate to contact us.
                    </p>
                  </div>
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
